import { z } from "zod";
import { isPstnNumber, normalizeDialString } from "./phone-number";
import { ROLE_LABELS, type Campaign, type Did, type Role, type RoutingSkill } from "./types";

/** CSV batches have a separate limit from pasted email invitations. */
export const MAX_CSV_INVITATIONS = 100;

export const INVITATION_CSV_TEMPLATE =
  'name,email,role,skills,campaign_slug,number\r\n' +
  'Jane Doe,jane@example.com,rep,setter:4;spanish:2,my-campaign,+13105550201\r\n';

const COLUMNS = ["name", "email", "role", "skills", "campaign_slug", "number"];

/** A US or Canadian number as `+1` and ten digits, however the sheet wrote it, or null. */
export function csvNumber(raw: string): string | null {
  if (!isPstnNumber(raw)) return null;
  return `+1${normalizeDialString(raw).replace(/\D/g, "").slice(-10)}`;
}

/** CSV records, including spreadsheet BOMs, escaped quotes, CRLF and quoted line breaks. */
export function csvRecords(source: string): string[][] {
  const text = source.replace(/^\uFEFF/, "");
  const records: string[][] = [];
  let record: string[] = [], field = "", quoted = false, closed = false;
  const finishField = () => { record.push(field.trim()); field = ""; closed = false; };
  const finishRecord = () => {
    finishField();
    if (record.some(Boolean)) records.push(record);
    record = [];
  };
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else { quoted = false; closed = true; }
      } else field += ch;
    } else if (ch === ',') finishField();
    else if (ch === '\n' || ch === '\r') {
      finishRecord();
      if (ch === '\r' && text[i + 1] === '\n') i++;
    } else if (ch === '"' && field === "" && !closed) quoted = true;
    else if (ch === '"' || (closed && ch.trim() !== "")) {
      throw new Error(`CSV record ${records.length + 1}: unexpected character beside a quoted field.`);
    } else if (!closed) field += ch;
  }
  if (quoted) throw new Error("CSV has an unclosed quoted field.");
  finishRecord();
  return records;
}

export interface CsvInvitePerson {
  email: string;
  display_name: string;
  /** Undefined where the row named none: the role chosen above the file is theirs. */
  role?: Role;
  campaign_ids: string[];
  campaign_slugs: string[];
  skills: { slug: string; level: number }[];
  /**
   * Their own number, E.164, where the row names one: it rings them on their first campaign once
   * they accept (ACL-176). The platform checks it is one of the tenant's numbers and routes nowhere.
   */
  number?: string;
}

/** Why a number cannot be held for a new rep, or null when it can: the platform's own checks, run
 *  in the preview (ACL-176). */
export function numberProblem(
  e164: string,
  dids: readonly Pick<Did, "number" | "action_type" | "held_for">[],
): string | null {
  const did = dids.find((d) => d.number === e164);
  if (!did) return `Number ${e164} is not one of your numbers.`;
  if (did.held_for) return `Number ${e164} is already held for ${did.held_for}'s invitation.`;
  if (did.action_type !== null) return `Number ${e164} already has a route. Pick an unrouted number.`;
  return null;
}

/** What a rep's own number does on the campaign it rings them on, in the words of its settings. */
export function numberEffect(
  campaign: Pick<Campaign, "own_calls_only_allowed" | "rep_number_caller_id"> | undefined,
): string {
  if (!campaign) return "";
  const calls = (campaign.own_calls_only_allowed ?? true)
    ? "They take only their own callers here: none of the line's other calls and no dialer leads."
    : "They still take the line's other calls and dialer leads too.";
  const callerId = campaign.rep_number_caller_id
    ? "Their dialer calls present this number."
    : "Their dialer calls do not present it (caller ID switch is off).";
  return `${calls} ${callerId}`;
}

export function parseInvitationCsv(
  text: string,
  campaigns: readonly Pick<Campaign, "id" | "slug" | "queue_id">[],
  skills: readonly Pick<RoutingSkill, "slug">[],
  /** The roles this admin may hand out. A row may name any of them; none means the one above. */
  roles: readonly Role[] = [],
  /** The tenant's numbers. Given, a row's number is checked against them before Send, so a typo,
   *  a routed number or a held one does not cost that person their invitation. */
  dids?: readonly Pick<Did, "number" | "action_type" | "held_for">[],
): { people: CsvInvitePerson[]; errors: string[] } {
  let records: string[][];
  try { records = csvRecords(text); }
  catch (e) { return { people: [], errors: [(e as Error).message] }; }
  const headers = records.shift()?.map((h) => h.toLowerCase()) ?? [];
  if (headers.length !== new Set(headers).size || headers.some((h) => !COLUMNS.includes(h)) ||
      !headers.includes("name") || !headers.includes("email")) {
    return { people: [], errors: [`Use unique columns: ${COLUMNS.join(", ")}. Name and email columns are required.`] };
  }
  if (!records.length || records.length > MAX_CSV_INVITATIONS) {
    return { people: [], errors: [`Include between 1 and ${MAX_CSV_INVITATIONS} people per CSV.`] };
  }
  const errors: string[] = [], people: CsvInvitePerson[] = [];
  const emails = new Set<string>();
  const numbers = new Map<string, number>();
  records.forEach((record, index) => {
    const fail = (message: string) => errors.push(`Row ${index + 2}: ${message}`);
    if (record.length !== headers.length) { fail("The number of values does not match the columns."); return; }
    const row = Object.fromEntries(headers.map((h, i) => [h, record[i]!]));
    const email = row.email!.toLowerCase();
    if (!z.string().email().safeParse(email).success) fail("Enter a valid email address.");
    if (emails.has(email)) fail(`Duplicate email: ${email}.`);
    emails.add(email);
    if (!row.name || row.name.length > 200) fail("Name must contain 1–200 characters.");
    const named = (row.role ?? "").trim().toLowerCase();
    let role: Role | undefined;
    if (named !== "") {
      if (!roles.includes(named as Role)) {
        fail(`Role '${row.role}' must be one of ${roles.map((r) => ROLE_LABELS[r].toLowerCase()).join(", ")}.`);
      } else role = named as Role;
    }
    const campaign_slugs = (row.campaign_slug ?? "").split(";").map((s) => s.trim()).filter(Boolean);
    const campaign_ids: string[] = [];
    for (const slug of new Set(campaign_slugs)) {
      const matches = campaigns.filter((c) => c.slug === slug);
      if (matches.length !== 1 || !matches[0]!.queue_id) fail(`Campaign '${slug}' is unavailable or has no staffing line.`);
      else campaign_ids.push(matches[0]!.id);
    }
    const assigned: CsvInvitePerson["skills"] = [];
    for (const entry of (row.skills ?? "").split(";").map((s) => s.trim()).filter(Boolean)) {
      const match = /^([^:]+):([1-5])$/.exec(entry);
      if (!match) { fail(`Skill '${entry}' must use slug:level, with a level from 1 to 5.`); continue; }
      const slug = match[1]!.trim();
      if (!skills.some((s) => s.slug === slug)) fail(`Skill '${slug}' is not an active rep skill.`);
      if (assigned.some((s) => s.slug === slug)) fail(`Skill '${slug}' is repeated.`);
      assigned.push({ slug, level: Number(match[2]) });
    }
    if (campaign_ids.length > 100 || assigned.length > 100) fail("At most 100 campaigns and 100 skills are allowed per person.");
    let number: string | undefined;
    const written = (row.number ?? "").trim();
    if (written !== "") {
      const e164 = csvNumber(written);
      if (e164 === null) fail(`Number '${written}' must be a US or Canadian phone number.`);
      else if (numbers.has(e164)) fail(`Number ${e164} is already given to row ${numbers.get(e164)}.`);
      else if (campaign_slugs.length === 0) fail(`Number ${e164} rings its rep on their first campaign; add a campaign_slug.`);
      else if (dids && numberProblem(e164, dids)) fail(numberProblem(e164, dids)!);
      else { number = e164; numbers.set(e164, index + 2); }
    }
    people.push({
      email, display_name: row.name!, ...(role ? { role } : {}), campaign_ids, campaign_slugs,
      skills: assigned, ...(number ? { number } : {}),
    });
  });
  return { people, errors };
}

/** Never silently select an arbitrary domain if the deployment is misconfigured. */
export function singleInviteDomain(domains: readonly { id: number }[]): number | null {
  return domains.length === 1 ? domains[0]!.id : null;
}
